// src/lib/fil-operateur.js — LE FIL, PARTAGÉ PAR TOUS LES ÉCRANS.
//
// ══ POURQUOI UN STORE, ET NON UN `useState` ════════════════════════════════
//
// Maquette du 2026-09-09 : « Sur les autres pages, la barre du bas se déplie en
// fil quand je clique dedans. MÊME conversation, MÊME historique. »
//
// Le bloc de l'accueil et la barre des autres pages sont deux composants qui ne
// sont jamais montés en même temps. Un état local mourrait au changement
// d'écran — et « et pour l'autre logement ? » ne marcherait plus dès qu'on
// bouge. Or c'est PRÉCISÉMENT quand on bouge qu'on pose ce genre de question.
//
// ══ CE QUI EST GARDÉ, ET OÙ ════════════════════════════════════════════════
//
// En mémoire, plus une copie dans `sessionStorage` : le fil survit à un
// rechargement de page, et meurt à la fermeture de l'onglet. C'est la bonne
// durée — une conversation d'hier n'a plus de contexte, et le relevé qu'elle
// citait est périmé.
//
// ⚠️ CE FIL N'EST PAS L'ARCHIVE — l'archive existe AILLEURS. Depuis le
// 2026-09-13 (Dimitri : « qu'il retienne tout, éternellement »), chaque
// échange est noté côté serveur dans `operateur_souvenirs`, APRÈS la réponse
// (lib/operateur-souvenirs.js). Ce fil reste la MÉMOIRE VIVE de la
// conversation en cours ; l'historique se relit par sujet et par jour. Le
// serveur reçoit `fil_id` pour ne pas ressortir au rappel ce qui est déjà ici.
//
// ⚠️ ET IL EST BORNÉ ICI AUSSI. Le serveur ne garde que les derniers tours,
// mais un fil qui grossit sans fin en mémoire finirait par ralentir l'écran —
// et par afficher une conversation que personne ne relira.

// La fenêtre pendant laquelle une carte reste « en attente », et de quoi
// composer le compte rendu d'une annulation. Voir « LE GESTE EN ATTENTE ».
import { FENETRE_GESTE_MS } from "./geste-empreinte";
import { composerAnnulation } from "./geste-fait";

const CLE = "fbz-fil-operateur";
// L'identifiant de la conversation en cours (voir « L'IDENTIFIANT DU FIL »).
const CLE_ID = "fbz-fil-operateur-id";
// Assez pour une session de travail, pas assez pour devenir un journal.
const MAX = 40;

let fil = [];
let filId = null;
const abonnes = new Set();

// ══ LES BRIEFS ORPHELINS ═══════════════════════════════════════════════════
//
// ⚠️ DÉFAUT SIGNALÉ PAR DIMITRI LE 2026-09-12, ET QUE JE NE POUVAIS PAS VOIR :
//
//   « J'ai toujours deux phrases dans l'opérateur. Pire : elles ne disent pas
//     la même chose. Une compte deux arrivées, l'autre une. »
//
// Le brief a vécu DANS le fil pendant quelques heures, avant d'en sortir pour
// avoir sa place propre. Les fils déjà écrits, eux, ont gardé ce tour — et
// `sessionStorage` le ressert à chaque ouverture. Dimitri voyait donc le brief
// d'AUJOURD'HUI en haut, et celui d'HIER dans le fil, figé avec ses chiffres
// de la veille.
//
// ⚠️ ET MON NAVIGATEUR DE VÉRIFICATION EST NEUF À CHAQUE FOIS : il n'a jamais
// eu ce fil, donc je n'ai jamais vu la seconde phrase. J'ai déclaré le point
// « fait » sur un écran qui n'était pas le sien. Vérifier à l'écran ne suffit
// pas — encore faut-il que ce soit le MÊME écran.
//
// La règle de nettoyage est sûre parce qu'elle est étroite : le brief n'entre
// plus jamais dans le fil, et une conversation COMMENCE toujours par une
// question de Dimitri. Un tour « lui » EN TÊTE ne peut donc être qu'un vestige.
// Les réponses de l'opérateur, elles, suivent toujours une question — elles ne
// sont jamais en tête, et rien ne les touche.
//
// ⚠️ ET UN TOUR « fait » EN TÊTE NON PLUS (2026-09-15). Un geste se clique sur
// une carte, qui naît d'une réponse, qui répond à une question : le compte
// rendu d'un geste ne peut pas ouvrir une conversation. En tête, c'est le même
// vestige — la moitié d'un échange dont le début a été coupé.
function _sansBriefOrphelin(v) {
  let i = 0;
  while (i < v.length && v[i] && (v[i].role === "lui" || v[i].role === "fait"))
    i += 1;
  return i === 0 ? v : v.slice(i);
}

function _lireStock() {
  try {
    const brut = window.sessionStorage.getItem(CLE);
    const v = brut ? JSON.parse(brut) : null;
    return Array.isArray(v) ? _sansBriefOrphelin(v).slice(-MAX) : [];
  } catch {
    // Fenêtre privée, stockage refusé, JSON abîmé : on repart à vide. Un fil
    // perdu est une gêne ; une exception ici casserait l'écran entier.
    return [];
  }
}

function _ecrireStock() {
  try {
    window.sessionStorage.setItem(CLE, JSON.stringify(fil));
  } catch {
    /* quota ou stockage refusé : le fil vit en mémoire, c'est suffisant */
  }
}

function _prevenir() {
  for (const fn of abonnes) {
    try {
      fn(fil);
    } catch (e) {
      // Un abonné qui plante ne doit pas empêcher les autres d'être prévenus.
      console.error("[fil] abonné en échec", e?.message || e);
    }
  }
}

let charge = false;
/** Le fil courant. Charge le stockage au premier appel, jamais avant. */
export function lireFil() {
  if (!charge) {
    charge = true;
    fil = _lireStock();
  }
  return fil;
}

// ══ LES TROIS RÔLES ════════════════════════════════════════════════════════
//
// `moi` (Dimitri) · `lui` (l'opérateur) · `fait` — ce qu'un CLIC a donné.
//
// ⚠️ LE TROISIÈME EST ARRIVÉ LE 2026-09-15, et il n'est pas une voix : c'est un
// compte rendu, écrit PAR LE CODE après `executerGeste`, jamais par le modèle
// ni par Dimitri. Il vit dans le fil parce que c'est le seul endroit où il a du
// sens : au tour suivant, l'opérateur doit pouvoir dire « c'est fait » — et
// surtout ne pas reproposer ce qui vient d'être fait.
const ROLES = new Set(["moi", "lui", "fait"]);

/**
 * Ajoute un tour et prévient les écrans.
 * @param {'moi'|'lui'|'fait'} role
 * @param {string} texte
 * @param {object} [extra] — vue, destination, geste, compte rendu… ce que
 *   l'écran veut réafficher. Ce qui remonte au SERVEUR est choisi à part, par
 *   `filPourServeur`, et rien d'autre ne passe.
 */
export function ajouterAuFil(role, texte, extra) {
  if (!ROLES.has(role)) return lireFil();
  const t = typeof texte === "string" ? texte : "";
  // ⚠️ Un tour VIDE n'entre pas : il occuperait une place dans les six que le
  // serveur garde, et il ne dirait rien.
  if (!t.trim()) return lireFil();
  lireFil();
  fil = [...fil, { role, texte: t, quand: Date.now(), ...(extra || {}) }].slice(
    -MAX,
  );
  _ecrireStock();
  _prevenir();
  return fil;
}

// ══ LE GESTE EN ATTENTE ════════════════════════════════════════════════════
//
// Dimitri, 2026-09-15 : « le "oui" doit devenir le clic ». Pour cela, il faut
// savoir à quoi ce « oui » répond — et la réponse ne peut pas vivre dans un
// état d'écran : `envoyerQuestion` remet la réponse à zéro dès que la question
// part, donc la carte serait détruite à l'instant même où le « oui » est tapé.
//
// Elle vit donc ICI, dans le fil, attachée au tour qui l'a portée.
//
// ⚠️ CE QUI FERME L'ATTENTE — CORRIGÉ LE 2026-09-15 (revue adverse #1432,
// MAJEUR 3). La première version ne regardait QUE le dernier tour « lui ». Le
// piège, qui rendait la carte définitivement inatteignable :
//
//   carte prête → Dimitri écrit « ça marche » (ni oui ni non, ça part au
//   modèle) → le modèle recompose LE MÊME geste → la garde « déjà préparé »
//   le retire → la réponse arrive SANS carte → le dernier tour « lui » n'en
//   porte plus → plus rien en attente, et l'écran avait effacé la carte.
//
// Dimitri se retrouvait avec un « oui » qui répond « je n'ai rien en attente »
// pendant qu'il venait, littéralement, de la voir. On remonte donc le fil :
//   · un tour « fait » ferme TOUT ce qui précède — le geste est parti, ou
//     annulé, et un second « oui » le relancerait ;
//   · un tour « lui » PORTEUR d'un geste, c'est lui (s'il a moins de dix
//     minutes ; au-delà, la conversation a bougé) ;
//   · un tour « lui » dont la carte a été retirée parce qu'elle était DÉJÀ
//     préparée ne ferme rien : c'est le cas ci-dessus, et la carte est
//     toujours à l'écran ;
//   · tout autre tour « lui » ferme l'attente — il est passé à autre chose,
//     et l'écran a retiré la carte. L'attente et l'affichage disent alors la
//     même chose, ce qui est la seule façon qu'un « oui » ne soit pas une
//     surprise ;
//   · un fil vidé n'a rien en attente.

/** Le tour qui porte la carte en attente, quel que soit son âge. */
function _tourDuGeste(v) {
  for (let i = v.length - 1; i >= 0; i -= 1) {
    const t = v[i];
    if (!t) continue;
    // Un compte rendu ferme tout ce qui le précède.
    if (t.role === "fait") return null;
    if (t.role !== "lui") continue;
    if (t.geste && typeof t.geste === "object") return t;
    // La carte a été retirée par le serveur parce qu'elle est DÉJÀ sur la
    // table : on continue de remonter, elle est plus haut.
    if (t.geste_retire === DEJA_PREPARE) continue;
    // ⚠️ TOUT AUTRE MOTIF FERME L'ATTENTE, ET IL A UNE PHRASE (lot 0, 2026-09-22).
    // Le serveur retire aussi une carte quand il n'a pas su dire DE QUOI on
    // parle — « Je vois 2 logements pour "Cocon" : … — lequel ? ». Cette
    // phrase-là est PRÉFIXÉE au texte de la réponse côté serveur, donc elle est
    // déjà à l'écran ; ici, le motif sert à savoir qu'il n'y a plus de carte à
    // cliquer. Le distinguer explicitement, plutôt que de le laisser tomber
    // dans le `return null` d'en bas, évite qu'un lecteur pressé range les deux
    // cas ensemble le jour où il en ajoute un troisième.
    if (typeof t.geste_retire === "string" && t.geste_retire) return null;
    // ⚠️ UNE RÉPONSE NEUTRE NE FERME RIEN. Ce sont les phrases que l'écran
    // compose LUI-MÊME sur le chemin du « oui » — « à la voix je ne lance
    // rien », « il me faut le motif », « deux gestes sont prêts ». La carte
    // est toujours à l'écran : si ces tours fermaient l'attente, Dimitri
    // écrirait le motif demandé, dirait « oui », et s'entendrait répondre
    // « je n'ai rien en attente » — l'écran lui aurait demandé quelque chose
    // pour rien.
    if (t.neutre === true) continue;
    return null;
  }
  return null;
}

/** Le motif que le serveur pose quand il retire une carte en double. */
export const DEJA_PREPARE = "déjà préparé";

/**
 * La phrase à garder au tour quand le serveur a retiré la carte.
 *
 * ⚠️ « DÉJÀ PRÉPARÉ » N'EST PAS UN REFUS : la carte est toujours à l'écran, et
 * l'écran a besoin de ce mot exact pour le savoir. Les autres motifs sont des
 * phrases écrites par le code (« lequel ? »), déjà lisibles en tête de la
 * réponse. Les deux voyagent avec le tour, sous la même clé, et c'est ici
 * qu'on décide laquelle — une seule fonction, pour qu'un second motif ajouté
 * demain n'ait pas à être rebranché à trois endroits.
 */
export function motifDuRetrait(reponse) {
  const m = reponse && reponse.geste_retire;
  return typeof m === "string" && m ? m : null;
}

// ══ LE FORMULAIRE EN COURS ═════════════════════════════════════════════════
//
// L'état d'un formulaire (« inscris un nouveau propriétaire ») vit ICI, attaché
// au tour qui l'a produit — et nulle part ailleurs : aucune table, aucune
// session serveur. Il survit à un F5 (`sessionStorage`) et meurt avec l'onglet,
// ce qui est exactement la bonne durée pour un formulaire en cours.
//
// ⚠️ IL NE PART PAS PAR `filPourServeur` (allow-list ci-dessous) : il est envoyé
// À PART, dans le corps de la requête, comme `contexte` — une DÉCLARATION du
// navigateur, jamais une vérité. Le serveur la re-valide champ par champ
// (`lireEtatDeclare`) et JETTE ce qui n'entre pas au schéma.
//
// ⚠️ ET UN COMPTE RENDU FERME TOUT CE QUI LE PRÉCÈDE, exactement comme pour la
// carte en attente : un formulaire clôturé qui traînerait ferait re-proposer le
// même geste au tour suivant.

// ══ CE QUI FERME VRAIMENT UN FORMULAIRE : UNE ISSUE TERMINALE ══════════════
//
// ⚠️ DÉFAUT TROUVÉ À LA SECONDE REVUE DE #1436, ET IL NE SE VOYAIT PAS SUR LE
// CAS NOMINAL. Un compte rendu fermait le formulaire QUELLE QUE SOIT SON
// ISSUE — or `composerFait` en pose un aussi quand le geste a ÉCHOUÉ.
//
// Le scénario, et il est banal : Dimitri dicte neuf champs ; il a enregistré
// le parc dans un autre onglet entre-temps ; il clique « oui » ; le serveur
// refuse en 409 `cfg_conflit_version` — ce qui est exactement ce qu'on veut —
// et les neuf champs disparaissent sans un mot. La garde qui protège la
// configuration détruisait le travail qu'elle venait de sauver.
//
//   `fait`      → c'est écrit. Terminal.
//   `annule`    → il a dit non. Terminal.
//   `refuse`    → RIEN n'a été écrit (409, 403, 400) : le formulaire RESTE, et
//                 la carte peut repasser à l'identique.
//   `incertain` → on ne sait pas si c'est parti. Le formulaire RESTE (ses
//                 champs ne se perdent pas), mais l'empreinte, elle, est
//                 gardée côté serveur : on ne repropose PAS la même carte.
//                 Reproposer un geste qui a peut-être créé un propriétaire,
//                 c'est pousser à en créer un second.
//
// Une issue inconnue ferme, comme avant : ce qu'on ne sait pas lire ne laisse
// pas un formulaire ouvert indéfiniment.
const ISSUES_TERMINALES = new Set(["fait", "annule"]);

/** L'état du formulaire en cours, ou `null`. */
export function formulaireEnCours() {
  const v = lireFil();
  for (let i = v.length - 1; i >= 0; i -= 1) {
    const t = v[i];
    if (!t) continue;
    if (t.role === "fait") {
      const issue = t.fait && typeof t.fait === "object" ? t.fait.issue : null;
      // Un échec ne ferme rien : on continue à remonter vers l'état.
      if (issue === "refuse" || issue === "incertain") continue;
      if (!ISSUES_TERMINALES.has(issue)) return null;
      return null;
    }
    if (t.role !== "lui") continue;
    // ⚠️ `null` EXPLICITE FERME LE FORMULAIRE. Le serveur pose `formulaire:
    // null` quand il l'abandonne ou le clôture ; s'arrêter seulement sur une
    // valeur VRAIE ferait remonter l'état d'avant, et la conversation
    // reprendrait un formulaire que Dimitri vient d'annuler.
    if (Object.prototype.hasOwnProperty.call(t, "formulaire"))
      return t.formulaire || null;
    if (t.neutre === true) continue;
    return null;
  }
  return null;
}

/**
 * LE NOM QUI ATTEND UN « OUI », ou `null`.
 *
 * ⚠️ IL NE VIT QU'UN TOUR, et c'est ce qui le distingue d'un formulaire. Le
 * serveur demande « je comprends “Arrivée du jour — admin” : c'est bien
 * celui-là ? » et la réponse suivante tranche — oui, non, ou autre chose. On
 * lit donc le DERNIER tour de l'opérateur, pas le dernier qui en parle : une
 * confirmation d'il y a trois questions porterait sur un objet dont personne
 * ne parle plus, et un « oui » y répondrait par surprise.
 *
 * (C'est pourquoi `BlocOperateur` l'attache À CHAQUE tour, `null` compris.)
 */
export function confirmationEnCours() {
  const v = lireFil();
  for (let i = v.length - 1; i >= 0; i -= 1) {
    const t = v[i];
    if (!t || t.role !== "lui") continue;
    if (t.neutre === true) continue;
    return t.confirmation_attendue &&
      typeof t.confirmation_attendue === "object"
      ? t.confirmation_attendue
      : null;
  }
  return null;
}

/**
 * Le geste qu'un « oui » lancerait, ou `null`.
 * @param {number} [maintenant] — l'horloge, injectable pour les tests.
 */
export function gesteEnAttente(maintenant) {
  const tour = _tourDuGeste(lireFil());
  if (!tour) return null;
  const ref = Number.isFinite(maintenant) ? maintenant : Date.now();
  const quand = typeof tour.quand === "number" ? tour.quand : null;
  if (quand === null || ref - quand > FENETRE_GESTE_MS) return null;
  return tour.geste;
}

/**
 * L'instant où la carte en attente cessera de se lancer au « oui », ou `null`
 * s'il n'y en a pas.
 *
 * ⚠️ POURQUOI C'EST EXPORTÉ (revue adverse #1432, mineur 7) : `gesteEnAttente`
 * est lu AU RENDU, et rien ne re-rend l'écran quand la fenêtre se ferme. Sans
 * cette date, la carte resterait cliquable à l'identique pendant qu'un « oui »
 * répondrait « je n'ai rien en attente » — deux vérités à l'écran en même
 * temps. L'écran s'en sert pour se re-rendre à l'échéance, et pour le DIRE.
 */
export function expirationDuGeste() {
  const tour = _tourDuGeste(lireFil());
  const quand = tour && typeof tour.quand === "number" ? tour.quand : null;
  return quand === null ? null : quand + FENETRE_GESTE_MS;
}

/**
 * Ce geste a-t-il DÉJÀ été cliqué (ou annulé) dans cette conversation ?
 *
 * ⚠️ POUR LES CARTES QUI NE PASSENT PAS PAR LE FIL (revue #1432, seconde
 * passe). Une carte d'ÉVÉNEMENT n'est attachée à aucun tour « lui » : rien ne
 * disait donc qu'elle avait déjà servi, et un second « oui » la relançait — le
 * défaut exact qu'un compte rendu ferme du côté de la réponse. On compare les
 * EMPREINTES : elles disent « ce geste-là, avec ces arguments-là », sans rien
 * relire.
 *
 * @param {string} empreinte
 * @returns {boolean}
 */
export function estDejaFait(empreinte) {
  if (typeof empreinte !== "string" || !empreinte) return false;
  return lireFil().some(
    (t) =>
      t &&
      t.role === "fait" &&
      t.fait &&
      typeof t.fait === "object" &&
      t.fait.empreinte === empreinte,
  );
}

/**
 * Retire la carte en attente — Dimitri a dit « non ».
 *
 * ⚠️ ON POSE UN TOUR, ON N'EFFACE RIEN. Effacer le tour « lui » réécrirait la
 * conversation ; poser un compte rendu « annulé » la CONTINUE, et l'opérateur
 * sait au tour suivant que sa carte a été refusée — donc qu'il ne la
 * repropose pas comme si de rien n'était.
 *
 * @returns {boolean} vrai si une carte a bien été retirée.
 */
export function retirerGeste(motif) {
  const geste = gesteEnAttente();
  if (!geste) return false;
  const tour = composerAnnulation(geste, motif);
  if (!tour) return false;
  ajouterAuFil("fait", tour.texte, { fait: tour.fait });
  return true;
}

/** Efface le fil — la croix de la barre, et rien d'autre. */
export function viderFil() {
  fil = [];
  charge = true;
  _ecrireStock();
  // Un fil vidé est une conversation NOUVELLE : l'archive doit pouvoir
  // ressortir ce qu'on vient d'effacer de l'écran.
  filId = null;
  try {
    window.sessionStorage.removeItem(CLE_ID);
  } catch {
    /* rien à nettoyer */
  }
  _prevenir();
  return fil;
}

// ══ L'IDENTIFIANT DU FIL ═══════════════════════════════════════════════════
// Une conversation = un identifiant, posé au premier appel et gardé avec le
// fil (même durée de vie : l'onglet). Il ne sert qu'à une chose côté serveur :
// que le rappel des souvenirs NE RESSORTE PAS ce qui est déjà sous les yeux.
function _nouvelId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID)
      return crypto.randomUUID();
  } catch {
    /* pas de crypto : on tombe sur l'aléa simple */
  }
  return `f-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** L'identifiant de la conversation en cours — créé s'il n'existe pas. */
export function idDuFil() {
  if (filId) return filId;
  try {
    const v = window.sessionStorage.getItem(CLE_ID);
    if (v && /^[A-Za-z0-9_-]{1,64}$/.test(v)) {
      filId = v;
      return filId;
    }
  } catch {
    /* stockage refusé : un id en mémoire suffit */
  }
  filId = _nouvelId();
  try {
    window.sessionStorage.setItem(CLE_ID, filId);
  } catch {
    /* quota ou stockage refusé */
  }
  return filId;
}

/** S'abonner aux changements. Rend de quoi se désabonner. */
export function suivreFil(fn) {
  if (typeof fn !== "function") return () => {};
  abonnes.add(fn);
  return () => abonnes.delete(fn);
}

/**
 * Ce qu'on envoie au serveur : le rôle, le texte, et l'HEURE du tour.
 *
 * ⚠️ L'heure a été ajoutée le 2026-09-15, et elle paie sa place. Le fil du
 * jour se lit comme une journée : les tours se placent au moment où ils ont
 * été dits, et un tour d'un jour précédent porte son âge (« hier 16:44 »).
 * Sans `quand`, le serveur ne peut ni ordonner ni vieillir — il lisait une
 * question de la veille comme si elle venait d'être posée.
 * Un entier de 13 chiffres contre plusieurs centaines de caractères de texte :
 * le surcoût en jetons est négligeable devant ce qu'il rend lisible.
 *
 * ⚠️ Ni la vue, ni la destination : celles-là, le serveur n'en a pas besoin.
 *
 * ⚠️ NI LES ARGUMENTS D'UN GESTE (2026-09-15). Un tour `fait` porte ce qui a
 * été fait — le levier, son titre de registre, l'issue, le résumé rendu par le
 * serveur — et RIEN de ce avec quoi il l'a été. Le corps d'un `inbox_reply`
 * (4 000 caractères de texte destiné à un voyageur) n'a aucune raison de
 * revenir devant le modèle au tour suivant. La liste ci-dessous est donc une
 * ALLOW-LIST explicite, pas un `...t.fait` : ce qui s'ajoutera un jour au
 * compte rendu ne remontera pas tout seul.
 */
export function filPourServeur() {
  return lireFil().map((t) => ({
    role: t.role,
    texte: t.texte,
    quand: t.quand,
    // 2026-09-15 : ce qu'il a LU pour ce tour (lectures, bornes) — pour que
    // « qu'est-ce que tu as lu ? » se réponde par le code, depuis la trace.
    ...(t.trace && typeof t.trace === "object" ? { trace: t.trace } : {}),
    // L'EMPREINTE du geste préparé à ce tour — pas le geste. Une chaîne
    // opaque, qui ne dit ni les arguments ni le texte qu'ils portent.
    ...(t.role === "lui" && typeof t.geste_empreinte === "string"
      ? { geste_empreinte: t.geste_empreinte }
      : {}),
    ...(t.role === "fait" && t.fait && typeof t.fait === "object"
      ? {
          fait: {
            levier: t.fait.levier,
            titre: t.fait.titre,
            issue: t.fait.issue,
            etape: t.fait.etape,
            resume: t.fait.resume,
            // L'empreinte — une chaîne opaque, jamais les arguments. Elle
            // permet au serveur de retirer une carte qu'il vient de préparer
            // ou de faire, sans rien relire. Voir `lib/geste-empreinte.js`.
            empreinte: t.fait.empreinte,
          },
        }
      : {}),
  }));
}

/**
 * Le fil SANS la dernière réponse de l'opérateur — celle que l'écran rend déjà
 * en entier, sous le fil, avec sa vue et sa porte.
 *
 * ⚠️ POURQUOI UNE FONCTION, ET PAS UN `slice(0, -1)` (2026-09-15). Le découpage
 * par la fin tenait tant que le dernier tour était forcément la réponse. Depuis
 * qu'un tour `fait` peut la suivre, `slice(0, -1)` coupe le COMPTE RENDU et
 * laisse la réponse — qui s'affiche alors deux fois. On nomme donc ce qu'on
 * retire : le dernier tour « lui », où qu'il soit.
 */
export function filSansDerniereReponse(v) {
  const tours = Array.isArray(v) ? v : [];
  for (let i = tours.length - 1; i >= 0; i -= 1) {
    if (tours[i] && tours[i].role === "lui")
      return tours.filter((_, j) => j !== i);
  }
  return tours;
}

/** Tests uniquement — un fil qui survit d'un test à l'autre ment. */
export function _reinitialiser() {
  fil = [];
  charge = false;
  filId = null;
  abonnes.clear();
  try {
    window.sessionStorage.removeItem(CLE);
    window.sessionStorage.removeItem(CLE_ID);
  } catch {
    /* rien à nettoyer */
  }
}

export const _MAX = MAX;
export const _sansBriefOrphelinPourTest = _sansBriefOrphelin;
